import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  DatePicker,
  Select,
  Button,
  Space,
  Statistic,
  Tag,
  message,
  Tabs,
  Empty,
  Descriptions
} from 'antd';
import {
  DollarOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { stationOwnerAPI, analyticsAPI } from '../services/api';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

interface Payment {
  _id: string;
  amount: number;
  plan: string;
  paymentDate: string;
  status: 'pending' | 'completed' | 'failed';
  transactionId?: string;
}

const Financial: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [revenueData, setRevenueData] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [paymentsData, revenue] = await Promise.all([
        stationOwnerAPI.getPayments(),
        analyticsAPI.getRevenueReport(
          dateRange[0].format('YYYY-MM-DD'),
          dateRange[1].format('YYYY-MM-DD'),
          'day'
        )
      ]);
      setPayments(paymentsData);
      setRevenueData(revenue);
      
      // Özet hesapla
      const completedPayments = paymentsData.filter((p: Payment) => p.status === 'completed');
      const totalPaid = completedPayments.reduce((sum: number, p: Payment) => sum + p.amount, 0);
      const pendingPayments = paymentsData.filter((p: Payment) => p.status === 'pending');
      const totalPending = pendingPayments.reduce((sum: number, p: Payment) => sum + p.amount, 0);
      
      setSummary({
        totalPaid,
        totalPending,
        completedCount: completedPayments.length,
        pendingCount: pendingPayments.length,
        totalRevenue: revenue.summary?.totalRevenue || 0
      });
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const paymentColumns = [
    {
      title: 'Tarih',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: string) => dayjs(date).format('DD MMMM YYYY, HH:mm'),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan: string) => {
        const planValue = plan || 'basic';
        return (
          <Tag color={planValue === 'premium' ? 'gold' : planValue === 'basic' ? 'blue' : 'default'}>
            {planValue === 'premium' ? 'Premium' : planValue === 'basic' ? 'Temel' : 'Ücretsiz'}
          </Tag>
        );
      },
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount.toLocaleString('tr-TR')} ₺`,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig: any = {
          completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Tamamlandı' },
          pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'Beklemede' },
          failed: { color: 'error', icon: <CloseCircleOutlined />, text: 'Başarısız' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'İşlem ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      render: (id: string) => id || '-',
    },
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <DollarOutlined /> Finansal Yönetim
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            Gelir takibi, ödemeler ve finansal raporlar
          </Text>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              style={{ width: '100%', maxWidth: 300 }}
            />
            <Space wrap style={{ width: '100%' }}>
              <Button icon={<ReloadOutlined />} onClick={loadData}>
                Yenile
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => message.info('PDF export yakında eklenecek')}>
                PDF İndir
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Gelir"
                value={summary.totalRevenue || 0}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Ödenen Tutar"
                value={summary.totalPaid || 0}
                prefix={<CheckCircleOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary.completedCount} ödeme
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Bekleyen Ödeme"
                value={summary.totalPending || 0}
                prefix={<ClockCircleOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{ color: '#faad14' }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary.pendingCount} ödeme
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Net Gelir"
                value={(summary.totalRevenue || 0) - (summary.totalPaid || 0)}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
                valueStyle={{ color: '#722ed1' }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Gelir - Ödemeler
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      <Tabs defaultActiveKey="payments">
        <TabPane
          tab={
            <span>
              <CreditCardOutlined />
              Ödeme Geçmişi
            </span>
          }
          key="payments"
        >
          <Card>
            <Table
              columns={paymentColumns}
              dataSource={payments}
              rowKey="_id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <BarChartOutlined />
              Gelir Analizi
            </span>
          }
          key="revenue"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card title="Gelir Trendi">
                {revenueData?.data && revenueData.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={revenueData.data}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#52c41a" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value: any) => [`${value.toLocaleString('tr-TR')} ₺`, 'Gelir']} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#52c41a"
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                        name="Gelir (₺)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Veri bulunamadı" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Günlük İşlem Sayısı">
                {revenueData?.data && revenueData.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueData.data.slice(-14)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="transactions" fill="#1890ff" name="İşlem Sayısı" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Veri bulunamadı" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Ortalama İşlem Tutarı">
                {revenueData?.data && revenueData.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={revenueData.data.slice(-14)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value: any) => `${value.toLocaleString('tr-TR')} ₺`} />
                      <Legend />
                      <Line type="monotone" dataKey="averageTransaction" stroke="#faad14" name="Ortalama (₺)" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Veri bulunamadı" />
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Finansal Özet
            </span>
          }
          key="summary"
        >
          <Card>
            <Descriptions title="Finansal Özet" bordered column={2}>
              <Descriptions.Item label="Toplam Gelir">
                {summary?.totalRevenue?.toLocaleString('tr-TR') || 0} ₺
              </Descriptions.Item>
              <Descriptions.Item label="Toplam Ödeme">
                {summary?.totalPaid?.toLocaleString('tr-TR') || 0} ₺
              </Descriptions.Item>
              <Descriptions.Item label="Bekleyen Ödeme">
                {summary?.totalPending?.toLocaleString('tr-TR') || 0} ₺
              </Descriptions.Item>
              <Descriptions.Item label="Net Gelir">
                <Text strong style={{ color: '#52c41a' }}>
                  {((summary?.totalRevenue || 0) - (summary?.totalPaid || 0)).toLocaleString('tr-TR')} ₺
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tamamlanan Ödemeler">
                {summary?.completedCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Bekleyen Ödemeler">
                {summary?.pendingCount || 0}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Financial;

